import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useTimer } from '../../context/TimerContext';
import { useNavigate } from 'react-router-dom';
import {
  aiAssistantAPI,
  VoiceActionResult,
  PendingAiAction,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import VoiceInput, { VoiceListenState, isVoiceSupported } from './VoiceInput';
import {
  SparklesIcon,
  XIcon,
  PlusIcon,
  HistoryIcon,
  TrashAltIcon,
  SendPlaneIcon,
  TaskIcon,
  TimerIcon,
  NavigateIcon,
  FolderRoundedIcon,
  UsersIcon,
} from '../icons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatSession {
  id: string;
  label: string;
  date: string;
  messageCount: number;
}

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  actionResult?: VoiceActionResult;
}

export interface OperationalChatProps {
  /** Called when AI resolves a create_task action */
  onCreateTask?: (title: string, description?: string) => void;
  /** Called when AI resolves a create_project action */
  onCreateProject?: (name: string) => void;
  /** Called when AI resolves a create_client action */
  onCreateClient?: (name: string) => void;
  /** When provided, the header shows a close button (modal usage) */
  onClose?: () => void;
  className?: string;
}

// ---------------------------------------------------------------------------
// LocalStorage persistence helpers
// ---------------------------------------------------------------------------

const STORAGE_PREFIX = 'grantex_op_chat_';
const SESSIONS_KEY = 'grantex_op_sessions';
const ACTIVE_SESSION_KEY = 'grantex_op_active_session';

function filterActionJSON(content: string): string {
  const trimmed = content.trim();

  // Try to find and parse JSON anywhere in the content
  if (trimmed.includes('"action"')) {
    // Try to extract JSON starting from the first {
    const startIdx = trimmed.indexOf('{');
    if (startIdx !== -1) {
      const jsonCandidate = trimmed.substring(startIdx);
      try {
        // Try parsing the entire rest as JSON
        const parsed = JSON.parse(jsonCandidate);
        if (parsed.action) {
          // It's a valid action JSON — return friendly message or message field
          if (parsed.message) {
            return parsed.message;
          }
          // Return localized success message based on action type
          const actionMessages: Record<string, string> = {
            create_task: '✓ Task will be created',
            create_project: '✓ Project will be created',
            create_client: '✓ Client will be created',
            start_timer: '✓ Timer started',
            stop_timer: '✓ Timer stopped',
            navigate: '✓ Navigating...',
            log_time: '✓ Time logged',
          };
          return actionMessages[parsed.action] || '✓ Action executed';
        }
      } catch {
        // If that fails, try finding a complete JSON object by counting braces
        let braceCount = 0;
        let jsonEnd = -1;
        for (let i = startIdx; i < jsonCandidate.length; i++) {
          if (jsonCandidate[i] === '{') braceCount++;
          if (jsonCandidate[i] === '}') braceCount--;
          if (braceCount === 0 && i > startIdx) {
            jsonEnd = i + 1;
            break;
          }
        }
        if (jsonEnd > startIdx) {
          try {
            const parsed = JSON.parse(jsonCandidate.substring(0, jsonEnd));
            if (parsed.action) {
              if (parsed.message) {
                return parsed.message;
              }
              const actionMessages: Record<string, string> = {
                create_task: '✓ Task will be created',
                create_project: '✓ Project will be created',
                create_client: '✓ Client will be created',
                start_timer: '✓ Timer started',
                stop_timer: '✓ Timer stopped',
                navigate: '✓ Navigating...',
                log_time: '✓ Time logged',
              };
              return actionMessages[parsed.action] || '✓ Action executed';
            }
          } catch {
            // Still couldn't parse, return original
          }
        }
      }
    }
  }

  return content;
}

function generateSessionId(): string {
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function loadSessions(): ChatSession[] {
  try {
    return JSON.parse(localStorage.getItem(SESSIONS_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveSessions(sessions: ChatSession[]): void {
  try {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions.slice(0, 20)));
  } catch {
    /* storage full — ignore */
  }
}

function loadHistory(sessionId: string): ChatMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + sessionId);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(sessionId: string, messages: ChatMessage[]): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + sessionId, JSON.stringify(messages));
  } catch {
    /* storage full — ignore */
  }
}

// Backend AiConversation id tied to a UI session, so reopening the panel (or
// reloading the page) resumes the same server-side conversation history.
function loadConversationId(sessionId: string): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + sessionId + '_conv');
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

function saveConversationId(sessionId: string, id: number | null): void {
  try {
    if (id) {
      localStorage.setItem(STORAGE_PREFIX + sessionId + '_conv', String(id));
    } else {
      localStorage.removeItem(STORAGE_PREFIX + sessionId + '_conv');
    }
  } catch {
    /* storage full — ignore */
  }
}

// ---------------------------------------------------------------------------
// Inline SVG icons — no extra deps
// ---------------------------------------------------------------------------

const Icons = {
  assistant: <SparklesIcon className="w-5 h-5" strokeWidth={2} />,
  close: <XIcon className="w-5 h-5" strokeWidth={2} />,
  plus: <PlusIcon className="w-4 h-4" strokeWidth={2} />,
  history: <HistoryIcon className="w-4 h-4" strokeWidth={2} />,
  trash: <TrashAltIcon className="w-3.5 h-3.5" strokeWidth={2} />,
  send: <SendPlaneIcon className="w-4 h-4" strokeWidth={2} />,
  task: <TaskIcon className="w-4 h-4" strokeWidth={2} />,
  timer: <TimerIcon className="w-4 h-4" strokeWidth={2} />,
  navigate: <NavigateIcon className="w-4 h-4" strokeWidth={2} />,
  project: <FolderRoundedIcon className="w-4 h-4" strokeWidth={2} />,
  client: <UsersIcon className="w-4 h-4" strokeWidth={2} />,
};

// ---------------------------------------------------------------------------
// Quick action definitions
// ---------------------------------------------------------------------------

interface QuickAction {
  id: string;
  labelKey: string;
  icon: React.ReactNode;
  prompt: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'start_timer',
    labelKey: 'operational.actions.startTimer',
    icon: Icons.timer,
    prompt: 'Start my timer',
  },
  {
    id: 'stop_timer',
    labelKey: 'operational.actions.stopTimer',
    icon: Icons.timer,
    prompt: 'Stop my timer',
  },
  {
    id: 'create_task',
    labelKey: 'operational.actions.createTask',
    icon: Icons.task,
    prompt: 'Create a new task',
  },
  {
    id: 'create_project',
    labelKey: 'operational.actions.createProject',
    icon: Icons.project,
    prompt: 'Create a new project',
  },
  {
    id: 'create_client',
    labelKey: 'operational.actions.createClient',
    icon: Icons.client,
    prompt: 'Create a new client',
  },
  {
    id: 'go_timesheet',
    labelKey: 'operational.actions.goTimesheet',
    icon: Icons.navigate,
    prompt: 'Go to my timesheet',
  },
  {
    id: 'go_dashboard',
    labelKey: 'operational.actions.goDashboard',
    icon: Icons.navigate,
    prompt: 'Go to the dashboard',
  },
  {
    id: 'go_reports',
    labelKey: 'operational.actions.goReports',
    icon: Icons.navigate,
    prompt: 'Go to reports',
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const OperationalChat: React.FC<OperationalChatProps> = ({
  onCreateTask,
  onCreateProject,
  onCreateClient,
  onClose,
  className = '',
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { startTimer, stopTimer } = useTimer();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // ---- Session state -------------------------------------------------------
  const [sessions, setSessions] = useState<ChatSession[]>(() => loadSessions());
  const [currentSessionId, setCurrentSessionId] = useState<string>(() => {
    const active = sessionStorage.getItem(ACTIVE_SESSION_KEY);
    if (active) return active;
    const newId = generateSessionId();
    sessionStorage.setItem(ACTIVE_SESSION_KEY, newId);
    return newId;
  });
  const [showHistory, setShowHistory] = useState(false);

  // ---- Message state -------------------------------------------------------
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    loadHistory(currentSessionId),
  );
  const [streamingContent, setStreamingContent] = useState('');
  const streamingContentRef = useRef('');
  const [conversationId, setConversationId] = useState<number | null>(() =>
    loadConversationId(currentSessionId),
  );

  // ---- UI state ------------------------------------------------------------
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAiAction | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'chat' | 'actions'>('chat');
  const [voiceState, setVoiceState] = useState<VoiceListenState>('idle');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  // ---- Scroll to bottom on new content ------------------------------------
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  // ---- Persist messages + update session index ----------------------------
  useEffect(() => {
    if (messages.length === 0) return;
    saveHistory(currentSessionId, messages);
    const firstUser = messages.find((m) => m.role === 'user');
    const label =
      firstUser?.content.slice(0, 50) || t('operational.chat.newConversation');
    setSessions((prev) => {
      const without = prev.filter((s) => s.id !== currentSessionId);
      const updated = [
        {
          id: currentSessionId,
          label,
          date: new Date().toISOString(),
          messageCount: messages.length,
        },
        ...without,
      ].slice(0, 20);
      saveSessions(updated);
      return updated;
    });
  }, [messages, currentSessionId, t]);

  // ---- Close history panel on outside click --------------------------------
  useEffect(() => {
    if (!showHistory) return;
    const handler = (e: MouseEvent) => {
      if (
        historyRef.current &&
        !historyRef.current.contains(e.target as Node)
      ) {
        setShowHistory(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showHistory]);

  // ---- Keyboard shortcut: Ctrl/Cmd+M toggles mic ---------------------------
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'm') {
        e.preventDefault();
        if (voiceState === 'idle') setVoiceState('listening');
        else if (voiceState === 'listening') setVoiceState('idle');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [voiceState]);

  // ---------------------------------------------------------------------------
  // Session management
  // ---------------------------------------------------------------------------

  const handleNewConversation = useCallback(() => {
    const newId = generateSessionId();
    setCurrentSessionId(newId);
    setMessages([]);
    setStreamingContent('');
    streamingContentRef.current = '';
    setError(null);
    setConversationId(null);
    setPendingAction(null);
    sessionStorage.setItem(ACTIVE_SESSION_KEY, newId);
    setShowHistory(false);
  }, []);

  const handleSwitchSession = useCallback((id: string) => {
    setCurrentSessionId(id);
    setMessages(loadHistory(id));
    setConversationId(loadConversationId(id));
    setPendingAction(null);
    setStreamingContent('');
    streamingContentRef.current = '';
    setError(null);
    sessionStorage.setItem(ACTIVE_SESSION_KEY, id);
    setShowHistory(false);
  }, []);

  const handleDeleteSession = useCallback(
    (id: string) => {
      localStorage.removeItem(STORAGE_PREFIX + id);
      localStorage.removeItem(STORAGE_PREFIX + id + '_conv');
      setSessions((prev) => {
        const updated = prev.filter((s) => s.id !== id);
        saveSessions(updated);
        return updated;
      });
      if (id === currentSessionId) handleNewConversation();
    },
    [currentSessionId, handleNewConversation],
  );

  // ---------------------------------------------------------------------------
  // Action execution from AI response
  // ---------------------------------------------------------------------------

  const executeAction = useCallback(
    async (actionResult?: VoiceActionResult) => {
      if (!actionResult?.action || actionResult.action === 'unknown') return;

      try {
        switch (actionResult.action) {
          case 'start_timer':
            if (actionResult.project_id) {
              startTimer(actionResult.project_id, actionResult.task_id);
              showToast(
                t('operational.actions.timerStarted') || 'Timer started',
                'success',
              );
            }
            break;
          case 'stop_timer':
            stopTimer();
            showToast(
              t('operational.actions.timerStopped') || 'Timer stopped',
              'success',
            );
            break;
          case 'navigate':
            if (actionResult.destination) {
              // Map destinations to actual routes
              const routeMap: { [key: string]: string } = {
                timesheet: '/timesheet',
                tasks: '/manager/tasks',
                dashboard: '/manager',
                reports: '/manager/reports',
                profile: '/profile',
                projects: '/manager/projects',
                operational: '/manager/tasks',
              };
              const route =
                routeMap[actionResult.destination.toLowerCase()] ||
                `/${actionResult.destination}`;
              navigate(route);
            }
            break;
          // create_task / create_project / create_client / log_time are executed
          // by the backend inside the AI tool loop — the streamed reply is the
          // confirmation. Only UI actions are handled here.
          default:
            break;
        }
      } catch (err) {
        console.error('Action execution error:', err);
        showToast(t('common.error') || 'Action failed', 'error');
      }
    },
    [startTimer, stopTimer, navigate, showToast, t],
  );

  // ---------------------------------------------------------------------------
  // sendMessage — streams via aiAssistantAPI.streamChat with voice_mode=true
  // ---------------------------------------------------------------------------

  const sendMessage = useCallback(
    async (userMessage: string) => {
      if (!userMessage.trim() || isStreaming) return;

      setError(null);
      // A new message supersedes any action still awaiting confirmation
      // (the backend clears it too).
      setPendingAction(null);
      const trimmed = userMessage.trim();

      const userMsg: ChatMessage = {
        id: Date.now(),
        role: 'user',
        content: trimmed,
        createdAt: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setIsStreaming(true);
      streamingContentRef.current = '';
      setStreamingContent('');

      try {
        await aiAssistantAPI.streamChat(
          {
            message: trimmed,
            context_type: 'operational',
            voice_mode: true,
            conversation_id: conversationId || undefined,
          },
          // onChunk — accumulate streamed text
          (chunk: string) => {
            streamingContentRef.current += chunk;
            setStreamingContent(streamingContentRef.current);
          },
          // onDone — commit streamed content as assistant message
          (
            chatConversationId: number,
            actionResult?: VoiceActionResult,
            clientActions?: VoiceActionResult[],
            pendingConfirmation?: PendingAiAction,
          ) => {
            setPendingAction(pendingConfirmation || null);
            // Store conversation ID for subsequent messages and persist it so
            // reopening the panel resumes the same backend conversation.
            if (chatConversationId) {
              setConversationId(chatConversationId);
              saveConversationId(currentSessionId, chatConversationId);
            }

            const assistantMsg: ChatMessage = {
              id: Date.now() + 1,
              role: 'assistant',
              content: filterActionJSON(streamingContentRef.current),
              createdAt: new Date().toISOString(),
              actionResult,
            };

            setMessages((prev) => [...prev, assistantMsg]);
            streamingContentRef.current = '';
            setStreamingContent('');
            setIsStreaming(false);

            const actions =
              clientActions && clientActions.length > 0
                ? clientActions
                : actionResult
                  ? [actionResult]
                  : [];
            for (const action of actions) {
              void executeAction(action);
            }
          },
          // onError
          (errorMsg: string) => {
            setError(errorMsg || t('operational.chat.errorMessage'));
            streamingContentRef.current = '';
            setStreamingContent('');
            setIsStreaming(false);
          },
        );
      } catch (err: any) {
        setError(err?.message || t('operational.chat.errorMessage'));
        streamingContentRef.current = '';
        setStreamingContent('');
        setIsStreaming(false);
      }
    },
    [isStreaming, executeAction, t, conversationId, currentSessionId],
  );

  // ---------------------------------------------------------------------------
  // Input handlers
  // ---------------------------------------------------------------------------

  // Execute or cancel the action awaiting confirmation (server-side gate).
  const handleConfirmAction = useCallback(
    async (approve: boolean) => {
      if (!conversationId || !pendingAction) return;
      setPendingAction(null);
      setIsStreaming(true);
      try {
        const res = await aiAssistantAPI.confirmAction(conversationId, approve);
        const assistantMsg: ChatMessage = {
          id: Date.now(),
          role: 'assistant',
          content: res.data.response,
          createdAt: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMsg]);
      } catch (err: any) {
        setError(
          err?.response?.data?.error || t('operational.chat.errorMessage'),
        );
      } finally {
        setIsStreaming(false);
      }
    },
    [conversationId, pendingAction, t],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = input.trim();
    if (!msg) return;
    setInput('');
    sendMessage(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const msg = input.trim();
      if (!msg) return;
      setInput('');
      sendMessage(msg);
    }
  };

  const handleVoiceTranscript = useCallback(
    (text: string) => {
      setInput(text);
      sendMessage(text);
    },
    [sendMessage],
  );

  const handleQuickAction = (action: QuickAction) => {
    setActiveTab('chat');
    sendMessage(action.prompt);
  };

  // ---------------------------------------------------------------------------
  // Theme-derived class helpers
  // ---------------------------------------------------------------------------

  const bg = isDark ? 'bg-slate-900' : 'bg-white';
  const border = isDark ? 'border-slate-700' : 'border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-slate-900';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-500';
  const inputBg = isDark
    ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500 focus:border-blue-500'
    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-blue-500';
  const tabActiveCls = isDark
    ? 'border-b-2 border-blue-400 text-blue-400'
    : 'border-b-2 border-blue-600 text-blue-700';
  const tabInactiveCls = isDark
    ? 'text-slate-400 hover:text-slate-200'
    : 'text-slate-500 hover:text-slate-700';
  const assistantBubble = isDark
    ? 'bg-slate-800 text-slate-200 border border-slate-700'
    : 'bg-slate-50 text-slate-800 border border-slate-200';
  const historyPanelCls = isDark
    ? 'bg-slate-800 border border-slate-700 shadow-xl'
    : 'bg-white border border-slate-200 shadow-xl';
  const actionBtnCls = isDark
    ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700'
    : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 hover:border-blue-300';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      className={`flex flex-col h-full rounded-2xl border overflow-hidden ${bg} ${border} ${className}`}
    >
      {/* ------------------------------------------------------------------ */}
      {/* Header                                                               */}
      {/* ------------------------------------------------------------------ */}
      <div
        className="px-4 py-3 flex items-center justify-between shrink-0"
        style={{
          background:
            'linear-gradient(135deg, #1e3a5f 0%, #1e40af 60%, #0e7490 100%)',
        }}
      >
        <div className="flex items-center gap-2.5 text-white">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
            {Icons.assistant}
          </div>
          <div>
            <p className="text-sm font-semibold leading-tight">
              {t('operational.chat.title')}
            </p>
            <p className="text-xs text-blue-200 leading-tight">
              {t('operational.chat.subtitle')}
            </p>
          </div>
        </div>

        {/* Session controls */}
        <div className="flex items-center gap-1" ref={historyRef}>
          {/* History dropdown trigger */}
          <div className="relative">
            <button
              onClick={() => setShowHistory((v) => !v)}
              disabled={isStreaming}
              className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40"
              title={t('operational.chat.history')}
              aria-label={t('operational.chat.history')}
            >
              {Icons.history}
            </button>

            {showHistory && (
              <div
                className={`absolute right-0 top-8 z-50 w-64 rounded-xl max-h-64 overflow-y-auto ${historyPanelCls}`}
              >
                <div
                  className={`p-2 border-b ${isDark ? 'border-slate-700' : 'border-slate-100'}`}
                >
                  <button
                    onClick={handleNewConversation}
                    className={`w-full text-left text-xs font-semibold rounded px-2 py-1.5 transition-colors flex items-center gap-1.5 ${
                      isDark
                        ? 'hover:bg-slate-700 text-blue-400'
                        : 'hover:bg-slate-50 text-blue-600'
                    }`}
                  >
                    {Icons.plus}
                    {t('operational.chat.newConversation')}
                  </button>
                </div>

                {sessions.length === 0 ? (
                  <p className={`text-xs p-3 text-center ${textMuted}`}>
                    {t('operational.chat.noHistory')}
                  </p>
                ) : (
                  <div className="p-1">
                    {sessions.map((s) => (
                      <div
                        key={s.id}
                        className={`flex items-center justify-between rounded-lg px-2 py-1.5 text-xs transition-colors ${
                          s.id === currentSessionId
                            ? isDark
                              ? 'bg-blue-500/20 text-blue-300 font-medium'
                              : 'bg-blue-50 text-blue-700 font-medium'
                            : isDark
                              ? 'hover:bg-slate-700 text-slate-300'
                              : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <button
                          onClick={() => handleSwitchSession(s.id)}
                          className="flex-1 text-left truncate"
                        >
                          {s.label}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSession(s.id);
                          }}
                          className={`shrink-0 ml-1 transition-colors ${
                            isDark
                              ? 'text-slate-600 hover:text-red-400'
                              : 'text-slate-400 hover:text-red-500'
                          }`}
                          aria-label={t('operational.chat.deleteSession')}
                        >
                          {Icons.trash}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* New conversation */}
          <button
            onClick={handleNewConversation}
            disabled={isStreaming}
            className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40"
            title={t('operational.chat.newConversation')}
            aria-label={t('operational.chat.newConversation')}
          >
            {Icons.plus}
          </button>

          {/* Close (modal usage only) */}
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              title={t('common.close')}
              aria-label={t('common.close')}
            >
              {Icons.close}
            </button>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Tabs                                                                 */}
      {/* ------------------------------------------------------------------ */}
      <div className={`flex border-b shrink-0 ${border}`}>
        <button
          onClick={() => setActiveTab('chat')}
          className={`flex-1 py-2 text-xs font-semibold uppercase tracking-wide transition-colors ${
            activeTab === 'chat' ? tabActiveCls : tabInactiveCls
          }`}
        >
          {t('operational.tabs.chat')}
        </button>
        <button
          onClick={() => setActiveTab('actions')}
          className={`flex-1 py-2 text-xs font-semibold uppercase tracking-wide transition-colors ${
            activeTab === 'actions' ? tabActiveCls : tabInactiveCls
          }`}
        >
          {t('operational.tabs.actions')}
        </button>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Actions tab                                                          */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'actions' && (
        <div
          className={`flex-1 overflow-y-auto p-4 ${
            isDark ? 'bg-slate-900/50' : 'bg-slate-50'
          }`}
        >
          <p className={`text-xs mb-3 ${textMuted}`}>
            {t('operational.actions.hint')}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {QUICK_ACTIONS.map((action) => (
              <button
                key={action.id}
                disabled={isStreaming}
                onClick={() => handleQuickAction(action)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${actionBtnCls}`}
              >
                <span className={isDark ? 'text-blue-400' : 'text-blue-600'}>
                  {action.icon}
                </span>
                <span className="truncate">{t(action.labelKey)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Chat tab — always mounted to preserve streaming state               */}
      {/* ------------------------------------------------------------------ */}
      <div
        className={
          activeTab === 'chat' ? 'flex flex-col flex-1 min-h-0' : 'hidden'
        }
      >
        {/* Message list */}
        <div
          className={`flex-1 overflow-y-auto p-4 space-y-4 ${
            isDark ? 'bg-slate-900/50' : 'bg-slate-50'
          }`}
        >
          {/* Empty state */}
          {messages.length === 0 && !isStreaming && (
            <div className="text-center py-10">
              <div
                className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 ${
                  isDark
                    ? 'bg-blue-900/40 text-blue-400'
                    : 'bg-blue-50 text-blue-600'
                }`}
              >
                <SparklesIcon className="w-7 h-7" strokeWidth={1.75} />
              </div>
              <h3 className={`text-base font-semibold mb-1 ${textPrimary}`}>
                {t('operational.chat.welcome')}
              </h3>
              <p className={`text-sm max-w-xs mx-auto ${textMuted}`}>
                {t('operational.chat.welcomeMessage')}
              </p>
              {/* Suggestion chips */}
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {(
                  t('operational.chat.suggestions', {
                    returnObjects: true,
                  }) as string[]
                ).map((suggestion: string, i: number) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(suggestion)}
                    className={`px-3 py-1.5 text-xs rounded-xl transition-all duration-200 ${
                      isDark
                        ? 'bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700'
                        : 'bg-white hover:bg-slate-50 text-slate-600 border border-slate-200 hover:border-blue-300'
                    }`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Message bubbles */}
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${
                msg.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                    : assistantBubble
                }`}
              >
                <p className="text-sm whitespace-pre-wrap break-words">
                  {msg.content}
                </p>

                {/* Action badge */}
                {msg.actionResult && msg.actionResult.action !== 'unknown' && (
                  <div
                    className={`mt-1.5 text-xs px-2 py-0.5 rounded-full inline-block ${
                      msg.role === 'user'
                        ? 'bg-white/20 text-white/80'
                        : isDark
                          ? 'bg-blue-500/20 text-blue-300'
                          : 'bg-blue-50 text-blue-700'
                    }`}
                  >
                    {msg.actionResult.message || msg.actionResult.action}
                  </div>
                )}

                <div
                  className={`text-xs mt-1 ${
                    msg.role === 'user'
                      ? 'text-white/60'
                      : isDark
                        ? 'text-slate-500'
                        : 'text-slate-400'
                  }`}
                >
                  {new Date(msg.createdAt).toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}

          {/* Streaming bubble */}
          {isStreaming && (
            <div className="flex justify-start">
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${assistantBubble}`}
              >
                {streamingContent ? (
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {filterActionJSON(streamingContent)}
                  </p>
                ) : (
                  <div className="flex gap-1 items-center h-5">
                    {[0, 100, 200].map((delay) => (
                      <span
                        key={delay}
                        className={`w-2 h-2 rounded-full animate-bounce ${
                          isDark ? 'bg-slate-500' : 'bg-slate-400'
                        }`}
                        style={{ animationDelay: `${delay}ms` }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Confirmation card — mutating action awaiting user approval */}
          {pendingAction && !isStreaming && (
            <div
              className={`rounded-xl p-3 border ${
                isDark
                  ? 'bg-amber-500/10 border-amber-500/25'
                  : 'bg-amber-50 border-amber-200'
              }`}
            >
              <p
                className={`text-sm font-medium mb-1 ${
                  isDark ? 'text-amber-300' : 'text-amber-800'
                }`}
              >
                {t('operational.confirm.title', {
                  defaultValue: 'Confirm this action?',
                })}
              </p>
              <p
                className={`text-xs mb-3 break-words ${
                  isDark ? 'text-slate-400' : 'text-slate-600'
                }`}
              >
                {pendingAction.tool}
                {' — '}
                {Object.entries(pendingAction.arguments || {})
                  .map(([k, v]) => `${k}: ${String(v)}`)
                  .join(' · ')}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleConfirmAction(true)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                >
                  {t('operational.confirm.approve', {
                    defaultValue: 'Confirm',
                  })}
                </button>
                <button
                  onClick={() => handleConfirmAction(false)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                    isDark
                      ? 'border-slate-600 text-slate-300 hover:bg-slate-700/50'
                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {t('operational.confirm.cancel', {
                    defaultValue: 'Cancel',
                  })}
                </button>
              </div>
            </div>
          )}

          {/* Error banner */}
          {error && (
            <div
              className={`rounded-xl p-3 text-sm ${
                isDark
                  ? 'bg-rose-500/10 border border-rose-500/20 text-rose-400'
                  : 'bg-rose-50 border border-rose-200 text-rose-700'
              }`}
            >
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Input area                                                         */}
        {/* ---------------------------------------------------------------- */}
        <div className={`p-3 border-t shrink-0 ${border}`}>
          <form onSubmit={handleSubmit} className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('operational.chat.placeholder')}
              disabled={isStreaming}
              rows={1}
              className={`flex-1 px-3 py-2.5 rounded-xl resize-none border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 text-sm ${inputBg}`}
            />

            {isVoiceSupported && (
              <VoiceInput
                state={voiceState}
                onStateChange={setVoiceState}
                onTranscript={handleVoiceTranscript}
                disabled={isStreaming}
              />
            )}

            <button
              type="submit"
              disabled={!input.trim() || isStreaming}
              title={t('operational.chat.send')}
              aria-label={t('operational.chat.send')}
              className="p-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white shadow-lg shadow-blue-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center shrink-0"
            >
              {Icons.send}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OperationalChat;
